import { CloseOutlined } from "@ant-design/icons";
import { Button, Radio } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

type GroupBySelectorProps = {
  api?: {
    closeToolPanel?: () => void;
    setRowGroupColumns?: (colIds: string[]) => void;
    getRowGroupColumns?: () => Array<{
      getColId?: () => string;
      colId?: string;
    }>;
    addEventListener?: (eventType: string, listener: () => void) => void;
    removeEventListener?: (eventType: string, listener: () => void) => void;
    isDestroyed?: () => boolean;
  };
  context?: {
    materialHeaderMap?: {
      trade_partner?: string;
      division?: string;
    };
  };
};

const GroupBySelector = ({ api, context }: GroupBySelectorProps) => {
  const [selectedOption, setSelectedOption] = useState<string>("none");
  const divisionLabel = context?.materialHeaderMap?.division || "Division";
  const tradePartnerLabel =
    context?.materialHeaderMap?.trade_partner || "Responsible Contractor";
  const groupByOptions = useMemo(() => {
    const noneOption = { value: "none", label: "None", colId: "none" };
    const sortableOptions = [
      { value: "division", label: divisionLabel, colId: "division" },
      {
        value: "trade_partner",
        label: tradePartnerLabel,
        colId: "trade_partner"
      }
    ].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );

    return [noneOption, ...sortableOptions];
  }, [divisionLabel, tradePartnerLabel]);

  const applyGrouping = (value: string) => {
    const selectedGroupByOption = groupByOptions.find(
      (option) => option.value === value
    );
    const selectedColId = selectedGroupByOption?.colId;

    if (!selectedColId || selectedColId === "none") {
      api?.setRowGroupColumns?.([]);
      return;
    }

    api?.setRowGroupColumns?.([selectedColId]);
  };

  const syncSelectedOptionFromGrid = useCallback(() => {
    const selectedRowGroupColId =
      api?.getRowGroupColumns?.()?.[0]?.getColId?.() ??
      api?.getRowGroupColumns?.()?.[0]?.colId;

    if (!selectedRowGroupColId) {
      setSelectedOption("none");
      return;
    }

    const selectedGroupByOption = groupByOptions.find(
      (option) => option.colId === selectedRowGroupColId
    );
    setSelectedOption(selectedGroupByOption?.value ?? "none");
  }, [api, groupByOptions]);

  useEffect(() => {
    syncSelectedOptionFromGrid();

    const handleRowGroupChange = () => {
      syncSelectedOptionFromGrid();
    };

    if (api && !api.isDestroyed?.()) {
      api.addEventListener?.("columnRowGroupChanged", handleRowGroupChange);
    }

    return () => {
      if (api && !api.isDestroyed?.()) {
        api.removeEventListener?.(
          "columnRowGroupChanged",
          handleRowGroupChange
        );
      }
    };
  }, [api, syncSelectedOptionFromGrid]);

  return (
    <div>
      <div className="flex items-center justify-between pl-3 pr-1.5 py-2.5 border-0 border-b border-solid border-ciq-gray-400">
        <div className="text-sm font-medium">Group By</div>
        <Button
          type="text"
          aria-label="Close group by panel"
          onClick={() => api?.closeToolPanel?.()}
          className="group !min-w-0 !border-none !bg-transparent !px-1 hover:!bg-transparent"
        >
          <CloseOutlined className="!text-ciq-black-a45 group-hover:!text-ciq-black-a58" />
        </Button>
      </div>
      <div className="w-full py-2">
        <Radio.Group
          className="w-full"
          value={selectedOption}
          onChange={(event) => {
            const value = event.target.value;
            setSelectedOption(value);
            applyGrouping(value);
          }}
        >
          <div className="flex flex-col">
            {groupByOptions.map((option) => {
              const isSelected = selectedOption === option.value;
              const showClearButton = isSelected && option.value !== "none";

              return (
                <div
                  key={option.value}
                  className="flex h-[38px] items-center pl-3 pr-3 py-1 hover:bg-ciq-gray-200"
                >
                  <Radio
                    value={option.value}
                    className="!flex min-w-0 flex-1 overflow-hidden [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1 [&>span:last-child]:overflow-hidden p-0.5"
                  >
                    <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs ">
                      {option.label}
                    </span>
                  </Radio>
                  {showClearButton ? (
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={
                        <CloseOutlined className="!text-red-500 group-hover:!text-red-600" />
                      }
                      aria-label="Clear grouping"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedOption("none");
                        applyGrouping("none");
                      }}
                      className="group shrink-0 !min-w-0 !px-1 !text-base"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </Radio.Group>
      </div>
    </div>
  );
};

export default GroupBySelector;
